import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Person as TeacherIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  LocalHospital as SickIcon,
  BeachAccess as LeaveIcon,
  Work as OfficialIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface TeacherAttendanceRecord {
  teacher__emp_no: string;
  teacher__first_name: string;
  teacher__last_name: string;
  teacher__position: string;
  status: string;
  arrival_time: string | null;
  departure_time: string | null;
  hours_worked: number | null;
  classes_missed: number;
  students_affected: number;
  reason: string | null;
  school__name: string;
}

interface TeacherAttendanceData {
  date: string;
  total_teachers: number;
  status_summary: Record<string, number>;
  impact_summary: {
    classes_missed: number;
    students_affected: number;
  };
  attendance_records: TeacherAttendanceRecord[];
}

const TeacherAttendanceDetails: React.FC = () => {
  const [attendanceData, setAttendanceData] = useState<TeacherAttendanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSchool, setSelectedSchool] = useState<string>('');
  const [schools, setSchools] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchTeacherAttendance();
  }, [selectedDate, selectedSchool]);

  const fetchFilterOptions = async () => {
    try {
      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.getFilterOptions();
      if (response.data.status === 'success') {
        setSchools(response.data.data.schools || []);
      }
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const fetchTeacherAttendance = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');

      const response = await attendanceAPI.getTeacherAttendance({
        start_date: selectedDate.toISOString().split('T')[0],
        end_date: selectedDate.toISOString().split('T')[0],
        school: selectedSchool || undefined,
      });

      if (response.data.status === 'success') {
        setAttendanceData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load teacher attendance data');
      }
    } catch (err: any) {
      console.error('Teacher attendance error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load teacher attendance data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'present':
        return <PresentIcon color="success" />;
      case 'absent':
        return <AbsentIcon color="error" />;
      case 'sick_leave':
        return <SickIcon color="info" />;
      case 'annual_leave':
      case 'maternity_leave':
        return <LeaveIcon color="info" />;
      case 'official_duty':
        return <OfficialIcon color="warning" />;
      default:
        return <TeacherIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status.toLowerCase()) {
      case 'present':
        return 'success';
      case 'absent':
      case 'unauthorized':
        return 'error';
      case 'official_duty':
        return 'warning';
      case 'sick_leave':
      case 'annual_leave':
      case 'maternity_leave':
      case 'study_leave':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const calculateAttendanceRate = () => {
    if (!attendanceData || attendanceData.total_teachers === 0) return 0;
    const presentCount = attendanceData.status_summary.present || 0;
    return (presentCount / attendanceData.total_teachers * 100).toFixed(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Teacher Attendance Details
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={(newValue) => newValue && setSelectedDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>School</InputLabel>
                <Select
                  value={selectedSchool}
                  label="School"
                  onChange={(e) => setSelectedSchool(e.target.value)}
                >
                  <MenuItem value="">All Schools</MenuItem>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id.toString()}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={fetchTeacherAttendance}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {attendanceData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Overall Attendance
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {calculateAttendanceRate()}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {attendanceData.status_summary.present || 0} of {attendanceData.total_teachers} present
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <PresentIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6">Present</Typography>
                  </Box>
                  <Typography variant="h3" color="success.main">
                    {attendanceData.status_summary.present || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <SchoolIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="h6">Classes Missed</Typography>
                  </Box>
                  <Typography variant="h3" color="error.main">
                    {attendanceData.impact_summary.classes_missed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <TeacherIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6">Students Affected</Typography>
                  </Box>
                  <Typography variant="h3" color="warning.main">
                    {attendanceData.impact_summary.students_affected}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Status Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Breakdown
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(attendanceData.status_summary).map(([status, count]) => (
                  <Grid item key={status}>
                    <Chip
                      icon={getStatusIcon(status)}
                      label={`${formatStatus(status)}: ${count}`}
                      color={getStatusColor(status)}
                      variant="outlined"
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Attendance Records Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Teacher Attendance Records - {attendanceData.date}
              </Typography>

              {attendanceData.attendance_records.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee ID</TableCell>
                        <TableCell>Teacher Name</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Hours Worked</TableCell>
                        <TableCell>Classes Missed</TableCell>
                        <TableCell>Students Affected</TableCell>
                        <TableCell>Arrival</TableCell>
                        <TableCell>Departure</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>School</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendanceData.attendance_records.map((record, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{record.teacher__emp_no}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {getStatusIcon(record.status)}
                              <Box ml={1}>
                                {record.teacher__first_name} {record.teacher__last_name}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{record.teacher__position}</TableCell>
                          <TableCell>
                            <Chip
                              label={formatStatus(record.status)}
                              color={getStatusColor(record.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {record.hours_worked ? `${record.hours_worked}h` : '-'}
                          </TableCell>
                          <TableCell>
                            {record.classes_missed > 0 ? (
                              <Chip
                                label={record.classes_missed}
                                color="error"
                                size="small"
                              />
                            ) : (
                              '0'
                            )}
                          </TableCell>
                          <TableCell>
                            {record.students_affected > 0 ? (
                              <Chip
                                label={record.students_affected}
                                color="warning"
                                size="small"
                              />
                            ) : (
                              '0'
                            )}
                          </TableCell>
                          <TableCell>{record.arrival_time || '-'}</TableCell>
                          <TableCell>{record.departure_time || '-'}</TableCell>
                          <TableCell>{record.reason || '-'}</TableCell>
                          <TableCell>{record.school__name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No teacher attendance records found for the selected date and filters.
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default TeacherAttendanceDetails;